import React from 'react';
import PropTypes from 'prop-types';
import Page from 'Common/Layout/Page';
import Section from 'Common/Layout/Section';
import DataTable from 'Common/Table/DataTable';
import Panel from 'Common/Layout/Panel';
import ExportCsvButton from 'Common/Buttons/ExportCsvButton';
import YearSelector from 'Common/Components/year-selector/YearSelector.container';
import TypeSelector from '../Components/TypeSelector/TypeSelector';
import classes from './TemplateList.module.css';
import ActionButton from 'Common/Buttons/ActionButton';
import { startsWith } from 'utils';

const keyGetter = row => row.markingTeamId + ':' + row.markingTeamTitle;
const ignoreZeros = (val, col, base) => val === 0 || startsWith(val, '0001-01-01') ? '' : base(val, col);

const schema = [
    { field: 'markingTeamId', label: 'ID', forDisplay: false },
    { field: 'button', label: 'Amend', type: 'button', align: 'left', className: 'button_column' },
    { field: 'templateName', label: 'Template' },
    { field: 'markingTeamTitle', label: 'Team' },
    { field: 'amendmentNumber', label: 'Amendment Number', type: 'number', format: ignoreZeros },
    { field: 'amendmentDate', label: 'Amendment Date', type: 'date', format: ignoreZeros }
];

const exportSchema = [
    { field: 'qualificationYear', label: 'Year' },
    { field: 'appointmentType', label: 'Type' },
    { field: 'templateName', label: 'Template' },
    { field: 'markingTeamTitle', label: 'Team' },
    { field: 'amendmentNumber', label: 'Amendment Number', type: 'number' },
    { field: 'amendmentDate', label: 'Amendment Date', type: 'date' }
];

function TemplateListView({
    allowFilterSubmit,
    amendEnabled,
    dataSource,
    dataExportSource,
    dataExportAllSource,
    grouping,
    selected,
    showData,
    showExport,
    title,
    type,
    year,
    onAmendClick,
    onFilterSubmit,
    onSelectionChange,
    onTypeChange,
    onTypesLoaded,
    onYearChange
}) {
    return (
        <Page title={title} fitScreen>
            <Section>
                <h3 class="sr-only">Search</h3>
                <Panel title="Filter">
                    <div className={classes.filter}>
                        <YearSelector year={year} grouping={grouping} updateYear={onYearChange} numberOfFutureYears={1} />
                        {year && (
                            <div className={classes['filter__control']}>
                                <TypeSelector
                                    year={year}
                                    value={type}
                                    grouping={grouping}
                                    onChange={onTypeChange}
                                    onLoad={onTypesLoaded}
                                />
                            </div>
                        )}
                        <div className={classes['filter__commands']}>
                            {allowFilterSubmit && <ActionButton text="Go" onClick={onFilterSubmit} />}
                        </div>
                    </div>
                </Panel>
            </Section>
            {showData && (
                <>
                    <Section>
                        <h3 class="sr-only">Search Results</h3>
                        <DataTable
                            dataSource={dataSource}
                            keyField={keyGetter}
                            schema={schema}
                            selection
                            pagination
                            selected={selected}
                            onSelectionChange={onSelectionChange}
                        />
                    </Section>
                    {showExport && (
                        <Section variant="commands">
                            <Section.Command>
                                <ExportCsvButton
                                    dataSource={dataExportSource}
                                    fileName={`amended_contracts_${type}.csv`}
                                    includeHeaders={true}
                                    schema={exportSchema}
                                    text={`Export ${type}`}
                                />
                            </Section.Command>
                            <Section.Command>
                                <ExportCsvButton
                                    dataSource={dataExportAllSource}
                                    fileName='amended_contracts_all_types.csv'
                                    includeHeaders={true}
                                    schema={exportSchema}
                                    text="Export All Types"
                                />
                            </Section.Command>
                        </Section>
                    )}
                </>
            )}
        </Page>
    );
}

TemplateListView.propTypes = {
    allowFilterSubmit: PropTypes.bool.isRequired,
    amendEnabled: PropTypes.bool,
    dataSource: PropTypes.any,
    dataExportSource: PropTypes.any,
    dataExportAllSource: PropTypes.any,
    dataLoading: PropTypes.bool,
    exportDataSource: PropTypes.func,
    grouping: PropTypes.string.isRequired,
    selected: PropTypes.object,
    showData: PropTypes.bool,
    showExport: PropTypes.bool,
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
    year: PropTypes.string,
    onAmendClick: PropTypes.func.isRequired,
    onFilterSubmit: PropTypes.func.isRequired,
    onSelectionChange: PropTypes.func.isRequired,
    onTypeChange: PropTypes.func.isRequired,
    onTypesLoaded: PropTypes.func.isRequired,
    onYearChange: PropTypes.func.isRequired
};

export default TemplateListView;